import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

export interface PickerOption {
  id: number
  label: string
  hint?: string
}

interface PickerDropdownProps {
  label: string
  options: PickerOption[]
  onPick: (id: number) => void
  disabled?: boolean
  emptyText?: string
  icon?: React.ReactNode
}

/** Lightweight click-to-open dropdown picker (button + panel + outside-click
 * backdrop). Used for "Add role" (user detail) and "Add user" (role members).
 * Includes a filter input when there are more than 6 options. */
export function PickerDropdown({
  label,
  options,
  onPick,
  disabled = false,
  emptyText = 'Nothing to add',
  icon,
}: PickerDropdownProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  return (
    <div className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-muted/80 transition-colors disabled:opacity-60"
      >
        {icon}
        {label}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <>
          {/* outside-click backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-60 max-h-72 overflow-y-auto rounded-lg border border-border bg-card shadow-xl p-1">
            {options.length > 6 && (
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter…"
                className="w-full mb-1 px-2 py-1 rounded border border-border bg-background text-sm"
              />
            )}
            {filtered.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onPick(opt.id)
                    setOpen(false)
                    setQuery('')
                  }}
                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
                >
                  <span className="block truncate">{opt.label}</span>
                  {opt.hint && <span className="block text-xs text-muted-foreground truncate">{opt.hint}</span>}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default PickerDropdown
